import { useQuery } from '@tanstack/react-query';

import { api } from '../lib/api-client';

import type { ConversationsFilters, InboxCounts, ListConversationsResponse } from '../lib/types';

/**
 * useConversations(filters, enabled) — React Query da listagem.
 *
 * Stale 30s (refresh manual via botão "Atualizar" no toolbar; WebSocket
 * entra Fatia 5). Cache pinned em filters serializados.
 *
 * `enabled` (Localizador Universal ⌘K): opt-out do CALLER, default `true` para
 * preservar todos os chamadores históricos. Sem ele é impossível gatear o
 * fetch, e a faceta «Conversa por ID» dispararia um request por tecla digitada
 * — «gatear o fetch, não só o render».
 */
export function useConversations(
  filters: ConversationsFilters,
  enabled = true,
): {
  data: ListConversationsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ['conversations', filters],
    queryFn: () => api.listConversations(filters),
    enabled,
    staleTime: 30 * 1000,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}

/**
 * useConversationCounts(filters) — contagens agregadas da Inbox (badges das
 * abas + por status do filter rail) via `GET /v1/conversations/counts`.
 *
 * queryKey sob o prefixo `['conversations']` → herda as invalidações WS da
 * listagem (badge atualiza junto com a lista).
 */
export function useConversationCounts(filters: ConversationsFilters): {
  data: InboxCounts | undefined;
} {
  const query = useQuery({
    queryKey: ['conversations', 'counts', filters],
    queryFn: () => api.getConversationCounts(filters),
    staleTime: 30 * 1000,
  });
  return { data: query.data?.data };
}
